import { useMemo, type ReactNode } from 'react';
import ReactECharts from 'echarts-for-react';
import { Card } from 'react-bootstrap';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import { useAppContext } from '@/Main';
import { getColor } from '@/helpers/utils';
import { PANEL_FONT_FAMILY } from '../../../utils/panel-font';
import { chartChrome, escapeHtml, themePrimary } from '../../../utils/competition-chart';
import { DashboardEmptyNote } from '../DashboardEmptyNote';
import { ChartLegend } from './ChartLegend';
import type {
  ProjectVisibilityPromptPerformance,
  ProjectVisibilityTopBrand,
  PromptListTopBrand,
} from '../../../types';

const MAX_TOOLTIP_PROMPTS = 3;

// Category hues validated for CVD separation; "neither" is a neutral gray.
const CATEGORY_DEFS = [
  { key: 'onlyYou', label: 'فقط شما', color: '#059669' as string | null },
  { key: 'both', label: 'هر دو', color: null as string | null },
  { key: 'onlyRival', label: 'فقط رقیب', color: '#d97706' as string | null },
  { key: 'neither', label: 'هیچ‌کدام', color: null as string | null },
] as const;

function gapCategories() {
  const primary = themePrimary();
  return CATEGORY_DEFS.map((category) => ({
    ...category,
    color: category.key === 'both' ? primary : category.color,
  }));
}

type CategoryKey = (typeof CATEGORY_DEFS)[number]['key'];

function sameBrand(brand: PromptListTopBrand, competitor: ProjectVisibilityTopBrand) {
  if (brand.knownBrandId && competitor.knownBrandId) {
    return brand.knownBrandId === competitor.knownBrandId;
  }
  return brand.canonicalName.toLowerCase() === competitor.canonicalName.toLowerCase();
}

export function PromptGapCard({
  prompts,
  competitors,
  footer,
  hideHeader,
}: {
  prompts: ProjectVisibilityPromptPerformance[];
  competitors: ProjectVisibilityTopBrand[];
  footer?: ReactNode;
  hideHeader?: boolean;
}) {
  const {
    config: { isDark, themeVariant },
  } = useAppContext() as { config: { isDark: boolean; themeVariant?: string } };

  const rows = useMemo(() => {
    const executed = prompts.filter(
      (prompt) => prompt.status === 'executed' || prompt.status === 'partial',
    );
    return competitors.map((competitor) => {
      const buckets: Record<CategoryKey, string[]> = {
        onlyYou: [],
        both: [],
        onlyRival: [],
        neither: [],
      };
      for (const prompt of executed) {
        const brands = prompt.brands ?? prompt.topBrands;
        const you = brands.some((brand) => brand.relationship === 'you');
        const rival = brands.some((brand) => sameBrand(brand, competitor));
        const key: CategoryKey =
          you && rival ? 'both' : you ? 'onlyYou' : rival ? 'onlyRival' : 'neither';
        buckets[key].push(prompt.text);
      }
      return { name: competitor.canonicalName, buckets };
    });
  }, [prompts, competitors]);

  const option = useMemo(() => {
    if (!rows.length) return null;
    const chrome = chartChrome();
    const categories = gapCategories();
    const neutral = getColor('gray-400');
    // Top-to-bottom reading order on a category y-axis needs reversed data.
    const ordered = [...rows].reverse();

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow', shadowStyle: { color: chrome.splitLine, opacity: 0.4 } },
        backgroundColor: chrome.tooltipBg,
        borderColor: chrome.tooltipBorder,
        borderWidth: 1,
        extraCssText: 'direction: rtl; text-align: right; max-width: 320px; white-space: normal;',
        textStyle: { color: chrome.tooltipText, fontSize: 12, fontFamily: PANEL_FONT_FAMILY },
        formatter: (params: Array<{ dataIndex: number }>) => {
          if (!params.length) return '';
          const row = ordered[params[0].dataIndex];
          const lines = categories.map((category) => {
            const color = category.color ?? neutral;
            return `<div style="margin-top:4px">
              <span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${color};margin-inline-end:6px"></span>
              ${category.label}: <strong>${row.buckets[category.key].length}</strong> پرامپت</div>`;
          }).join('');
          const missed = row.buckets.onlyRival.slice(0, MAX_TOOLTIP_PROMPTS);
          const missedList = missed.length
            ? `<div style="margin-top:8px;color:${chrome.muted}">پرامپت‌هایی که فقط رقیب آمده:</div>` +
              missed.map((text) => `<div style="margin-top:2px">• ${escapeHtml(text)}</div>`).join('') +
              (row.buckets.onlyRival.length > missed.length
                ? `<div style="color:${chrome.muted}">و ${row.buckets.onlyRival.length - missed.length} مورد دیگر</div>`
                : '')
            : '';
          return `<div style="font-family:${PANEL_FONT_FAMILY};font-size:12px;direction:rtl;text-align:right;color:${chrome.tooltipText}">
            <div style="font-weight:600">شما در برابر ${escapeHtml(row.name)}</div>${lines}${missedList}</div>`;
        },
      },
      legend: { show: false },
      // dir=ltr wrapper + explicit right gutter: RTL inherit clips y-labels otherwise.
      grid: { left: 12, right: 108, top: 8, bottom: 28, containLabel: false },
      xAxis: {
        type: 'value',
        inverse: true,
        minInterval: 1,
        splitLine: { lineStyle: { color: chrome.splitLine } },
        axisLabel: { color: chrome.axisLabel, fontSize: 11, fontFamily: PANEL_FONT_FAMILY },
      },
      yAxis: {
        type: 'category',
        position: 'right',
        data: ordered.map((row) => row.name),
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: {
          show: true,
          interval: 0,
          hideOverlap: false,
          color: chrome.strongText,
          fontSize: 11,
          fontFamily: PANEL_FONT_FAMILY,
          overflow: 'truncate',
          width: 96,
          margin: 8,
          align: 'left',
        },
      },
      series: categories.map((category) => ({
        name: category.label,
        type: 'bar',
        stack: 'prompts',
        barMaxWidth: 18,
        itemStyle: {
          color: category.color ?? neutral,
          borderColor: chrome.surface,
          borderWidth: 1,
        },
        emphasis: { focus: 'series' },
        data: ordered.map((row) => row.buckets[category.key].length),
      })),
    };
  }, [rows, isDark, themeVariant]);

  return (
    <Card className="h-100 shadow-sm d-flex flex-column">
      {hideHeader ? null : (
        <FalconCardHeader
          light
          titleTag="h6"
          className="py-3"
          title={
            <div>
              شکاف پرامپت‌ها با رقبا
              <p className="fs--2 text-700 mb-0 fw-normal mt-2">
                در هر پرامپت اجراشده، شما، رقیب یا هر دو دیده شده‌اید؟
              </p>
            </div>
          }
        />
      )}
      <Card.Body className="pt-2 flex-grow-1">
        {!option ? (
          <DashboardEmptyNote>در این بازه رقیبی در پاسخ‌های AI دیده نشده.</DashboardEmptyNote>
        ) : (
          <>
            <div dir="ltr">
              <ReactECharts
                key={`${isDark ? 'dark' : 'light'}-${themeVariant ?? 'primary'}`}
                option={option}
                notMerge
                style={{ height: Math.max(180, rows.length * 40 + 40) }}
                opts={{ renderer: 'svg' }}
              />
            </div>
            <ChartLegend
              items={gapCategories().map((category) => ({
                label: category.label,
                color: category.color ?? 'var(--falcon-gray-400)',
              }))}
            />
          </>
        )}
      </Card.Body>
      {footer}
    </Card>
  );
}
